// The People screen part: the nav item, /me/people (the list beside the open
// person), the events that change what it shows, the favorite kind, the key
// `g o` (People) and a person shown in a side view. Hosts add `personsPart`, or
// `personsScreen({ link })` to link people to accounts (cloud).
import type { QueryClient } from "@tanstack/react-query";
import { createRoute, Outlet, useParams } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { lazyScreen, withLazy } from "../app/lazy";
import type { MeRouterContext } from "../app/routes";
import { defineScreenPart, type SideViewProps } from "../app/screen-part";
import { invalidateMemo } from "../memos/queries";
import { refPrefix } from "../refs/queries";
import { ListDetail } from "../shell/list-detail";
import { iconOf } from "../ui/icon";
import {
  type PersonsExtensions,
  PersonsExtensionsProvider,
} from "./extensions";
import {
  invalidatePersons,
  personChanged,
  personDeleted,
  personQuery,
  personsPrefix,
} from "./queries";

// The screens are loaded when first opened (app/lazy.tsx).
const PeopleList = lazyScreen(() => import("./list"), "PeopleList");
const PersonRoute = lazyScreen(() => import("./detail"), "PersonRoute");
const PersonScreen = lazyScreen(() => import("./detail"), "PersonScreen");

const personIdOf = (payload: unknown) => {
  const id = (payload as { personId?: unknown } | null)?.personId;
  return typeof id === "string" ? id : undefined;
};

const noop = () => {};

const dropRef = (qc: QueryClient, id: string | undefined) => {
  if (id) void qc.invalidateQueries({ queryKey: refPrefix("person", id) });
};

function PeopleScreen() {
  const { personId } = useParams({ strict: false }) as { personId?: string };
  return (
    <ListDetail
      list={
        <PeopleList
          openPersonId={personId && personId !== "new" ? personId : null}
        />
      }
      detail={<Outlet />}
      showing={personId ? "detail" : "list"}
      listPath="/me/people"
      listLabel={m.persons_list()}
    />
  );
}

/** The part with what the host adds (cloud: linking a person to an account). */
export const personsScreen = (extensions: PersonsExtensions = {}) =>
  defineScreenPart({
    id: "people",
    nav: {
      order: 50,
      label: () => m.persons_title(),
      icon: iconOf("people"),
      to: "/me/people",
    },
    routes: (main) => {
      const people = createRoute({
        getParentRoute: () => main,
        path: "people",
        component: withLazy(
          () => (
            <PersonsExtensionsProvider extensions={extensions}>
              <PeopleScreen />
            </PersonsExtensionsProvider>
          ),
          PeopleList,
        ),
      });
      // Nothing open: the list is alone and no detail is drawn.
      const none = createRoute({ getParentRoute: () => people, path: "/" });
      const one = createRoute({
        getParentRoute: () => people,
        path: "$personId",
        loader: async ({ context, params }) => {
          if (params.personId === "new") return;
          const c = context as MeRouterContext;
          await c.queryClient
            .ensureQueryData(personQuery(c.api, params.personId))
            .catch(() => undefined);
        },
        component: PersonRoute,
      });
      return [people.addChildren([none, one])];
    },
    realtime: {
      "person.changed": (payload, { qc }) => {
        const id = personIdOf(payload);
        personChanged(qc, id);
        dropRef(qc, id);
      },
      "person.deleted": (payload, { qc }) => {
        const id = personIdOf(payload);
        personDeleted(qc, id);
        dropRef(qc, id);
      },
      // Waits and history of people come from Cases.
      "case.changed": (_payload, { qc }) => void invalidatePersons(qc),
      "case.deleted": (_payload, { qc }) => void invalidatePersons(qc),
      "label.changed": (_payload, { qc }) => void invalidatePersons(qc),
      "label.removed": (_payload, { qc }) => void invalidatePersons(qc),
      "agent_memo.changed": (payload, { qc }) => {
        const { kind, id } = (payload ?? {}) as {
          kind?: unknown;
          id?: unknown;
        };
        if (kind !== "person" || typeof id !== "string") return;
        void invalidateMemo(qc, "person", id);
        personChanged(qc, id);
      },
    },
    favoriteKinds: {
      person: { icon: iconOf("person"), to: (id) => `/me/people/${id}` },
    },
    sideViews: {
      person: function PersonSideView({ id }: SideViewProps) {
        return (
          <PersonsExtensionsProvider extensions={extensions}>
            <PersonScreen personId={id} onCreated={noop} />
          </PersonsExtensionsProvider>
        );
      },
    },
    shortcuts: [
      {
        id: "people.go",
        key: "g o",
        group: "navigate",
        label: () => m.persons_go(),
        run: ({ navigate }) => navigate("/me/people"),
      },
    ],
    persist: (key) =>
      key[0] === personsPrefix[0] &&
      key[1] === personsPrefix[1] &&
      (key[2] === "detail" || key[2] === "list"),
  });

/** The part as the offline edition has it (no accounts to link to). */
export const personsPart = personsScreen();
